/**
 * Portal del cliente.
 *
 * Lo que ve el titular de la instalación: en qué estado está su
 * cumplimiento y —esto es lo que ningún competidor enseña— **qué le toca
 * hacer a él**, con su artículo.
 *
 * Esa es la diferencia entre un portal de consulta y una herramienta útil.
 * El titular de una instalación tiene obligaciones propias que no son de
 * la mantenedora: mantener el acceso libre a los equipos, conservar la
 * documentación cinco años, encargar la inspección periódica. Si el portal
 * sólo enseñara «todo correcto» cuando la mantenedora ha hecho lo suyo,
 * estaría dando una tranquilidad falsa.
 */

import { useEffect, useState } from 'react';
import { ApiError } from '../session/api-error.js';
import { PortalSessionProvider, usePortalSession } from './portal-session.js';

interface OwnerDuty {
  duty: string;
  articleRef: string;
  status: 'AL_DIA' | 'PENDIENTE' | 'VENCIDO';
  detail: string;
}

interface SiteCard {
  siteId: string;
  siteName: string;
  level: string;
  totalEquipment: number;
  equipmentOk: number;
  equipmentOverdue: number;
  equipmentOutOfService: number;
  openAnomalies: number;
  criticalAnomalies: number;
  nextMaintenanceDate: string | null;
  ownerDuties: OwnerDuty[];
  headline: string;
}

interface Summary {
  sites: number;
  compliant: number;
  attention: number;
  nonCompliant: number;
  totalEquipment: number;
  openAnomalies: number;
}

const LEVEL: Record<string, { label: string; tone: string; icon: string }> = {
  CONFORME: { label: 'Conforme', tone: 'ok', icon: '✓' },
  ATENCION: { label: 'Requiere atención', tone: 'warn', icon: '◷' },
  NO_CONFORME: { label: 'No conforme', tone: 'bad', icon: '⚠' },
};

const DUTY: Record<string, { tone: string; icon: string }> = {
  AL_DIA: { tone: 'ok', icon: '✓' },
  PENDIENTE: { tone: 'warn', icon: '◷' },
  VENCIDO: { tone: 'bad', icon: '⚠' },
};

function LoginScreen(): React.ReactNode {
  const { login } = usePortalSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [taxId, setTaxId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password, taxId.trim() || undefined);
    } catch (caught) {
      // Igual que en el ERP: no se distingue si el correo existe.
      setError(
        caught instanceof ApiError && caught.status === 401
          ? 'Correo o contraseña incorrectos.'
          : caught instanceof ApiError
            ? caught.message
            : 'No se ha podido contactar con el servidor.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <form className="login__card" onSubmit={(e) => void submit(e)} noValidate>
        <header className="login__head">
          <p className="login__brand">Portal del cliente</p>
          <h1 className="login__title">Acceda a su cumplimiento</h1>
          <p className="login__hint">
            Consulte el estado de sus instalaciones de protección contra incendios y lo que le
            corresponde hacer como titular.
          </p>
        </header>

        {error !== null && (
          <p className="login__error" role="alert">
            {error}
          </p>
        )}

        <div className="field">
          <label htmlFor="portal-email">Correo electrónico</label>
          <input
            id="portal-email"
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="portal-password">Contraseña</label>
          <input
            id="portal-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="portal-taxid">
            NIF de su mantenedora <span className="field__optional">opcional</span>
          </label>
          <input id="portal-taxid" type="text" value={taxId} onChange={(e) => setTaxId(e.target.value)} />
          <p className="field__help">Sólo si trabaja con más de una empresa mantenedora.</p>
        </div>

        <button className="button button--primary" type="submit" disabled={busy}>
          {busy ? 'Comprobando…' : 'Entrar'}
        </button>
      </form>
    </main>
  );
}

function Dashboard(): React.ReactNode {
  const { api, user, logout } = usePortalSession();
  const [cards, setCards] = useState<SiteCard[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api
      .get<{ cards: SiteCard[]; summary: Summary }>('/portal/me/compliance')
      .then((data) => {
        if (cancelled) return;
        setCards(data.cards ?? []);
        setSummary(data.summary);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(
            caught instanceof ApiError ? caught.message : 'No se ha podido cargar su cumplimiento.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <div className="portal">
      <header className="topbar">
        <span className="topbar__brand">Portal del cliente</span>
        <div className="topbar__spacer" />
        {user && <span className="topbar__user">{user.fullName}</span>}
        <button className="button button--quiet" type="button" onClick={logout}>
          Salir
        </button>
      </header>

      <main className="content__body">
        <h1 className="screen__title">Estado de sus instalaciones</h1>

        {error !== null && (
          <p className="screen__error" role="alert">
            {error}
          </p>
        )}

        {loading && <p className="table__loading">Cargando…</p>}

        {summary && (
          <div className="cards">
            <div className="card">
              <p className="card__label">Centros</p>
              <p className="card__value">{summary.sites}</p>
            </div>
            <div className="card">
              <p className="card__label">Conformes</p>
              <p className="card__value">{summary.compliant}</p>
            </div>
            <div className={`card${summary.attention > 0 ? ' card--alert' : ''}`}>
              <p className="card__label">Requieren atención</p>
              <p className="card__value">{summary.attention}</p>
            </div>
            <div className={`card${summary.nonCompliant > 0 ? ' card--alert' : ''}`}>
              <p className="card__label">No conformes</p>
              <p className="card__value">{summary.nonCompliant}</p>
            </div>
          </div>
        )}

        {!loading && cards.length === 0 && error === null && (
          <p className="screen__note">
            No hay centros asociados a su acceso. Si cree que es un error, contacte con su empresa
            mantenedora.
          </p>
        )}

        {cards.map((card) => {
          const level = LEVEL[card.level] ?? LEVEL['ATENCION']!;
          return (
            <section className="form__section" key={card.siteId}>
              <div className="detail__title">
                <h2 className="form__section-title">{card.siteName}</h2>
                <span className={`pill pill--${level.tone}`}>{`${level.icon} ${level.label}`}</span>
              </div>

              <p className="form__section-note">{card.headline}</p>

              <div className="cards">
                <div className="card">
                  <p className="card__label">Equipos</p>
                  <p className="card__value">{card.totalEquipment}</p>
                  <p className="card__meta">{card.equipmentOk} al día</p>
                </div>
                <div className={`card${card.equipmentOverdue > 0 ? ' card--alert' : ''}`}>
                  <p className="card__label">Vencidos</p>
                  <p className="card__value">{card.equipmentOverdue}</p>
                </div>
                <div className={`card${card.equipmentOutOfService > 0 ? ' card--alert' : ''}`}>
                  <p className="card__label">Fuera de servicio</p>
                  <p className="card__value">{card.equipmentOutOfService}</p>
                  {card.equipmentOutOfService > 0 && (
                    <p className="card__warn">⚠ Estos equipos no protegen</p>
                  )}
                </div>
                <div className="card">
                  <p className="card__label">Próximo mantenimiento</p>
                  <p className="card__value">{card.nextMaintenanceDate ?? '—'}</p>
                </div>
              </div>

              {card.ownerDuties.length > 0 && (
                <>
                  <h3 className="screen__subtitle">Lo que le corresponde a usted como titular</h3>
                  <p className="form__section-note">
                    Estas obligaciones son del titular de la instalación, no de la empresa
                    mantenedora. Que su mantenimiento esté al día no las cubre.
                  </p>
                  <ul className="alerts">
                    {card.ownerDuties.map((duty, index) => {
                      const tone = DUTY[duty.status] ?? DUTY['PENDIENTE']!;
                      return (
                        <li className="alerts__item" key={`${card.siteId}-${index}`}>
                          <span className={`pill pill--${tone.tone}`}>{tone.icon}</span>
                          <div className="alerts__body">
                            <p className="alerts__message">{duty.duty}</p>
                            <p className="alerts__meta">
                              {duty.detail} · <em>{duty.articleRef}</em>
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </section>
          );
        })}
      </main>
    </div>
  );
}

function PortalRoot(): React.ReactNode {
  const { user } = usePortalSession();

  if (user === undefined) return <div className="booting">Comprobando la sesión…</div>;
  if (user === null) return <LoginScreen />;
  return <Dashboard />;
}

export function PortalApp(): React.ReactNode {
  return (
    <PortalSessionProvider>
      <PortalRoot />
    </PortalSessionProvider>
  );
}
